import {
  ClickTrackingContextType,
  ClickTrackingContextTypes,
} from "@max/common/analytics";
import { PropsWithChildren, useEffect, useRef } from "react";
import { useUserActionTrackingContext } from "./UserActionTrackingContext";

/**
 * Data attribute used to opt an element into click tracking. Authors of custom
 * HTML (e.g. the event footer) mark a trackable element with `data-set-track`.
 * Its value is recorded as the click `value`; when left empty we fall back to
 * the element's `href` and then its trimmed text content.
 *
 * The click `type` defaults to `"url"` and can be overridden with
 * `data-set-track-type` (must be a valid `ClickTrackingContextType`).
 *
 * @example
 *   <a href="https://example.com" data-set-track target="_blank">Visit</a>
 *   <a href="https://example.com" data-set-track="coors-banner">…</a>
 *   <a href="/merch" data-set-track="hoodie" data-set-track-type="merch">Shop</a>
 */
const TRACK_ATTRIBUTE = "data-set-track";

/**
 * Installs a global, delegated click handler for elements that opt into
 * tracking via the `data-set-track` attribute — notably links inside
 * creator-authored custom HTML, where we can't attach React handlers directly.
 *
 * Must be rendered inside `UserActionTrackingProvider`, since it relies on
 * `useUserActionTrackingContext` to record events.
 */
export const ClickTrackingProvider = ({ children }: PropsWithChildren) => {
  const { track } = useUserActionTrackingContext();

  // Keep the latest `track` in a ref so the global listener (bound once) always
  // calls the current closure without re-attaching on every render.
  const trackRef = useRef(track);
  trackRef.current = track;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const el = target?.closest?.<HTMLElement>(`[${TRACK_ATTRIBUTE}]`);

      if (!el) {
        return;
      }

      const requestedType = el.dataset.setTrackType;

      const type: ClickTrackingContextType = (
        ClickTrackingContextTypes as readonly string[]
      ).includes(requestedType ?? "")
        ? (requestedType as ClickTrackingContextType)
        : "url";

      const value =
        el.dataset.setTrack ||
        el.getAttribute("href") ||
        el.textContent?.trim() ||
        "";

      trackRef.current({ event: "click", context: { type, value } });
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  return <>{children}</>;
};
